/**
 * Sidebar — collapsible navigation rail.
 *
 * Two modes:
 *   - Expanded (w-60): icons + labels + active state with pill background
 *   - Collapsed (w-14): icons only; hovering an icon shows a flyout
 *                       containing the label (and sub-items if any)
 *
 * Mobile (< lg): the sidebar becomes an overlay drawer; collapsed-mode
 * doesn't apply on mobile (always full-expanded when shown).
 *
 * State persistence:
 *   `localStorage.ovacs.sidebarCollapsed` — string '1' | '0'
 *
 * Nav-item shape:
 *   {
 *     key:     'cases',
 *     label:   'Cases',         // i18n string already resolved
 *     icon:    HiOutlineFolder,
 *     href:    '/cases',
 *     show:    true,
 *     badge:   'Owner Only' | null,
 *     children: [ { key, label, href }, ... ] | undefined
 *   }
 *
 * Workspace switcher pill sits at the top of the sidebar (Notion pattern).
 * Bottom-pinned items: Settings, Contact Support, Collapse toggle.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineHome,
  HiOutlineUsers,
  HiOutlineFolder,
  HiOutlineClock,
  HiOutlineDocument,
  HiOutlineChatBubbleLeft,
  HiOutlineUserGroup,
  HiOutlineCog6Tooth,
  HiOutlineLifebuoy,
  HiOutlineXMark,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  HiOutlineChevronDown,
} from 'react-icons/hi2'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { useDirection } from '../../contexts/DirectionContext'
import { useSidebar } from '../../contexts/SidebarContext'
import ContactModal from '../common/ContactModal'
import DevelopedBy from '../common/DevelopedBy'

export default function Sidebar({ isOpen, onClose }) {
  const { t } = useTranslation()
  const { user, isWorkspaceSwitched, canManageConnections, ownerAccountName } = useAuth()
  const { getEntity } = useWorkspace()
  const { isRtl } = useDirection()
  const { collapsed, setCollapsed } = useSidebar()
  const location = useLocation()

  const [showContact, setShowContact] = useState(false)

  // Workspace display
  const displayName = isWorkspaceSwitched ? ownerAccountName : (user?.name || 'Workspace')
  const initial = (displayName || 'W').charAt(0).toUpperCase()

  // Build nav items in the hierarchy order: Dashboard · Clients · Cases · Sessions
  // · Documents · Messages — matching the resolved hierarchy decision (§16.1).
  const navItems = useMemo(() => [
    { key: 'dashboard', label: t('nav.dashboard'),                       icon: HiOutlineHome,           href: '/dashboard', show: true },
    { key: 'clients',   label: getEntity('client').plural,               icon: HiOutlineUsers,          href: '/clients',   show: true },
    { key: 'cases',     label: getEntity('case').plural,                 icon: HiOutlineFolder,         href: '/cases',     show: true },
    { key: 'sessions',  label: getEntity('session').plural,              icon: HiOutlineClock,          href: '/sessions',  show: true },
    { key: 'documents', label: getEntity('document').plural,             icon: HiOutlineDocument,       href: '/documents', show: true,
      children: [
        { key: 'all-docs', label: t('sidebar.allDocuments', { entity: getEntity('document').plural }), href: '/documents' },
        { key: 'groups',   label: t('sidebar.groups'),                                                  href: '/document-groups', show: !isWorkspaceSwitched },
      ].filter(c => c.show !== false),
    },
    { key: 'messages',    label: getEntity('message').plural,       icon: HiOutlineChatBubbleLeft, href: '/messages',  show: true },
    { key: 'connections', label: getEntity('connection').plural,    icon: HiOutlineUserGroup,      href: '/connections', show: canManageConnections, badge: t('workspace.owner') },
  ].filter(item => item.show), [t, getEntity, canManageConnections, isWorkspaceSwitched])

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    return location.pathname.startsWith(href)
  }

  // Width is the only thing Sidebar controls — positioning and slide
  // animation live in AppShell's wrapper so the two don't fight.
  // Mobile uses the expanded width when shown as a drawer.
  const widthClass = collapsed ? 'lg:w-14 w-60' : 'w-60'

  return (
    <>
      <aside
        className={`
          flex flex-col bg-canvas h-full
          ${widthClass}
          shrink-0 transition-[width] duration-200 ease-out
        `}
        aria-label="Primary navigation"
      >
        {/* Workspace pill — only shown when expanded. The OVACS logo lives
            in the topbar now (not here) so this section just identifies
            the active workspace. */}
        {!collapsed && (
          <div className="p-3 pt-4">
            <div className="p-2 rounded-lg border border-border bg-surface">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-brand-soft flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-brand">{initial}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-ink-primary truncate">{displayName}</p>
                  <p className="text-[10px] text-ink-muted truncate">
                    {isWorkspaceSwitched ? t('sidebar.memberView') : t('sidebar.yourWorkspace')}
                  </p>
                </div>
              </div>
            </div>

            {isWorkspaceSwitched && (
              <p className="mt-2 text-[10px] text-warning text-center font-medium">
                {t('sidebar.viewingAnotherWorkspace')}
              </p>
            )}
          </div>
        )}
        {/* When collapsed, leave a top spacer so the first nav icon
            doesn't kiss the topbar. */}
        {collapsed && <div className="pt-3" />}

        {/* Nav items. overflow-y-auto allows internal scrolling if items
            ever exceed the sidebar height. The hover flyouts in collapsed
            mode are portal-rendered into document.body so the overflow
            doesn't clip them. */}
        <nav className="flex-1 px-2 overflow-y-auto">
          <ul className="space-y-0.5">
            {navItems.map(item => (
              <SidebarItem
                key={item.key}
                item={item}
                collapsed={collapsed}
                isActive={isActive}
                isRtl={isRtl}
                onNavigate={() => { if (window.innerWidth < 1024) onClose?.() }}
              />
            ))}
          </ul>
        </nav>

        {/* Bottom pinned items */}
        <div className="p-2 border-t border-border-subtle space-y-0.5">
          <SidebarItem
            item={{ key: 'settings', label: t('nav.settings'), icon: HiOutlineCog6Tooth, href: '/profile-settings', show: true }}
            collapsed={collapsed}
            isActive={isActive}
            isRtl={isRtl}
            onNavigate={() => { if (window.innerWidth < 1024) onClose?.() }}
          />
          <SidebarAction
            label={t('sidebar.helpSupport')}
            icon={HiOutlineLifebuoy}
            collapsed={collapsed}
            isRtl={isRtl}
            onClick={() => setShowContact(true)}
          />

          {/* Collapse / expand toggle — desktop only */}
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            className="hidden lg:flex items-center w-full px-2.5 py-2 mt-1 rounded-lg text-ink-muted hover:bg-surface hover:text-ink-primary transition-colors"
          >
            {collapsed
              ? (isRtl ? <HiOutlineChevronDoubleLeft className="w-4 h-4 mx-auto" /> : <HiOutlineChevronDoubleRight className="w-4 h-4 mx-auto" />)
              : (
                <>
                  {isRtl
                    ? <HiOutlineChevronDoubleRight className="w-4 h-4 me-2" />
                    : <HiOutlineChevronDoubleLeft  className="w-4 h-4 me-2" />}
                  <span className="text-xs font-medium">{t('sidebar.collapse')}</span>
                </>
              )}
          </button>

          {/* Mobile close button (only visible below lg) */}
          <button
            onClick={onClose}
            className="flex lg:hidden items-center w-full px-2.5 py-2 rounded-lg text-ink-muted hover:bg-surface hover:text-ink-primary transition-colors"
          >
            <HiOutlineXMark className="w-4 h-4 me-2" />
            <span className="text-xs font-medium">{t('common.close')}</span>
          </button>
        </div>

        {/* Developer credit — the rail's footer, under the controls. */}
        <SidebarCredit collapsed={collapsed} />
      </aside>

      {/* Contact modal */}
      <ContactModal isOpen={showContact} onClose={() => setShowContact(false)} />
    </>
  )
}

// ────────────────────────────────────────────────────────────────────────
// useHoverFlyout — encapsulates the "show on hover, keep open while the
// pointer crosses the gap" pattern used by collapsed-sidebar tooltips.
// Returns refs + open state. Caller renders the trigger and the flyout.
// ────────────────────────────────────────────────────────────────────────
function useHoverFlyout() {
  const [open, setOpen] = useState(false)
  const closeTimerRef = useRef(null)
  const triggerRef = useRef(null)

  const show = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setOpen(true)
  }
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    // 100ms gives the pointer time to cross from trigger to flyout
    closeTimerRef.current = setTimeout(() => setOpen(false), 100)
  }
  const closeNow = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setOpen(false)
  }

  // Close on scroll/resize — the flyout is positioned relative to the
  // trigger's viewport rect, so any reflow would leave it dangling.
  useEffect(() => {
    if (!open) return undefined
    const close = () => closeNow()
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return { open, triggerRef, show, scheduleClose, closeNow }
}

// ────────────────────────────────────────────────────────────────────────
// FlyoutPortal — renders into document.body so it escapes any overflow
// or stacking context in the sidebar. Positioned against the trigger ref.
// ────────────────────────────────────────────────────────────────────────
function FlyoutPortal({ triggerRef, onMouseEnter, onMouseLeave, isRtl, children }) {
  const [pos, setPos] = useState(null)

  // useLayoutEffect runs synchronously after DOM commit but before browser
  // paint, so there's no flicker between FlyoutPortal mount and its first
  // positioned render.
  useLayoutEffect(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const gap = 8
    setPos({
      top: rect.top,
      // LTR: flyout sits to the right of the trigger; RTL: to the left
      left: isRtl ? null : rect.right + gap,
      right: isRtl ? window.innerWidth - rect.left + gap : null,
    })
  }, [triggerRef, isRtl])

  if (!pos) return null

  return createPortal(
    <div
      role="tooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left !== null ? pos.left : undefined,
        right: pos.right !== null ? pos.right : undefined,
        zIndex: 'var(--z-tooltip, 60)',
      }}
      className="min-w-[180px] bg-elevated border border-border rounded-lg shadow-token-lg py-1 px-1"
    >
      {children}
    </div>,
    document.body,
  )
}

// ────────────────────────────────────────────────────────────────────────
// SidebarItem — a nav item that may have children. Renders a NavLink in
// expanded mode and an icon-only button with a portal-rendered hover
// flyout when collapsed.
// ────────────────────────────────────────────────────────────────────────
function SidebarItem({ item, collapsed, isActive, isRtl, onNavigate }) {
  const { t } = useTranslation()
  const [submenuOpen, setSubmenuOpen] = useState(false)
  const flyout = useHoverFlyout()
  const Icon = item.icon
  const active = isActive(item.href)
  const hasChildren = item.children && item.children.length > 0

  // ─── Collapsed mode: icon button with portal flyout ───────────────────
  if (collapsed) {
    return (
      <li className="relative">
        <NavLink
          ref={flyout.triggerRef}
          to={item.href}
          onClick={(e) => { flyout.closeNow(); onNavigate?.(e) }}
          onMouseEnter={flyout.show}
          onMouseLeave={flyout.scheduleClose}
          onFocus={flyout.show}
          onBlur={flyout.scheduleClose}
          aria-label={item.label}
          className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${
            active
              ? 'bg-brand-soft text-brand'
              : 'text-ink-secondary hover:bg-surface hover:text-ink-primary'
          }`}
        >
          <Icon className="w-5 h-5" />
        </NavLink>

        {flyout.open && (
          <FlyoutPortal
            triggerRef={flyout.triggerRef}
            isRtl={isRtl}
            onMouseEnter={flyout.show}
            onMouseLeave={flyout.scheduleClose}
          >
            <div className="px-2 py-1.5 text-[11px] font-semibold text-ink-muted uppercase tracking-wider">
              {item.label}
            </div>
            {hasChildren ? (
              <ul className="space-y-0.5">
                {item.children.map(child => (
                  <li key={child.key}>
                    <NavLink
                      to={child.href}
                      onClick={(e) => { flyout.closeNow(); onNavigate?.(e) }}
                      className={({ isActive: a }) => `block px-2 py-1.5 rounded-md text-xs ${
                        a ? 'bg-brand-soft text-brand' : 'text-ink-primary hover:bg-muted'
                      }`}
                    >
                      {child.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : (
              <NavLink
                to={item.href}
                onClick={(e) => { flyout.closeNow(); onNavigate?.(e) }}
                className="block px-2 py-1.5 rounded-md text-xs text-ink-primary hover:bg-muted"
              >
                {t('sidebar.openItem', { label: item.label })}
              </NavLink>
            )}
          </FlyoutPortal>
        )}
      </li>
    )
  }

  // ─── Expanded mode: NavLink with optional inline submenu ──────────────
  return (
    <li>
      <div className="flex items-center">
        <NavLink
          to={item.href}
          onClick={onNavigate}
          className={`flex-1 flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
            active
              ? 'bg-brand-soft text-brand'
              : 'text-ink-secondary hover:bg-surface hover:text-ink-primary'
          }`}
        >
          <Icon className="w-4.5 h-4.5 flex-shrink-0" />
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-brand-soft text-brand">
              {item.badge}
            </span>
          )}
        </NavLink>
        {hasChildren && (
          <button
            type="button"
            onClick={() => setSubmenuOpen(o => !o)}
            aria-label={submenuOpen ? t('sidebar.hideSubmenu') : t('sidebar.showSubmenu')}
            className="p-1.5 rounded text-ink-muted hover:bg-surface hover:text-ink-primary"
          >
            <HiOutlineChevronDown className={`w-3.5 h-3.5 transition-transform ${submenuOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {hasChildren && submenuOpen && (
        <ul className="ms-7 mt-0.5 space-y-0.5 border-s border-border-subtle ps-2">
          {item.children.map(child => (
            <li key={child.key}>
              <NavLink
                to={child.href}
                onClick={onNavigate}
                className={({ isActive: a }) => `block px-2.5 py-1.5 rounded-md text-xs transition-colors ${
                  a ? 'bg-brand-soft text-brand font-medium' : 'text-ink-secondary hover:bg-surface hover:text-ink-primary'
                }`}
              >
                {child.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

// ────────────────────────────────────────────────────────────────────────
// SidebarAction — like SidebarItem but for actions (no href, runs onClick).
// Used by Help & Support and similar bottom items.
// ────────────────────────────────────────────────────────────────────────
function SidebarAction({ label, icon: Icon, collapsed, isRtl, onClick }) {
  const flyout = useHoverFlyout()

  if (collapsed) {
    return (
      <div className="relative">
        <button
          ref={flyout.triggerRef}
          type="button"
          onClick={(e) => { flyout.closeNow(); onClick?.(e) }}
          onMouseEnter={flyout.show}
          onMouseLeave={flyout.scheduleClose}
          onFocus={flyout.show}
          onBlur={flyout.scheduleClose}
          aria-label={label}
          className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg text-ink-secondary hover:bg-surface hover:text-ink-primary transition-colors"
        >
          <Icon className="w-5 h-5" />
        </button>

        {flyout.open && (
          <FlyoutPortal
            triggerRef={flyout.triggerRef}
            isRtl={isRtl}
            onMouseEnter={flyout.show}
            onMouseLeave={flyout.scheduleClose}
          >
            <span className="px-2 py-1.5 text-xs font-medium text-ink-primary whitespace-nowrap block">
              {label}
            </span>
          </FlyoutPortal>
        )}
      </div>
    )
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm font-medium text-ink-secondary hover:bg-surface hover:text-ink-primary transition-colors"
    >
      <Icon className="w-4.5 h-4.5 flex-shrink-0" />
      <span className="flex-1 truncate text-start">{label}</span>
    </button>
  )
}

// ────────────────────────────────────────────────────────────────────────
// SidebarCredit — "Developed by Stirringapps" as the rail's footer line, the
// size of the rail's small text. Only in the expanded rail (and the mobile
// drawer): the collapsed rail has no room for a line of text and shows nothing.
// ────────────────────────────────────────────────────────────────────────
function SidebarCredit({ collapsed }) {
  if (collapsed) return null
  return (
    <div className="px-4 py-3 border-t border-border-subtle text-xs">
      <DevelopedBy tone="auto" />
    </div>
  )
}
